import { BookedParty, Coupon, SkatingSession } from '@types';
import * as XLSX from 'xlsx';

export interface ExcelExportData {
  date: string;
  time: string;
  delivery: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  guestOfHonor: string;
  notes: string;
  packageName: string;
  pizzaList: string;
  [key: string]: string | number; // For dynamic extras columns
}

export interface SkatingSessionExportData {
  date: string;
  time: string;
  sessionNumber: number;
  barcodeId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  waiver: string;
  redeemed: string;
}

export interface CouponExportData {
  couponCode: string;
  name: string;
  discountType: string;
  discountValue: number;
  maxUses: number;
  maxUsesPerUser: number;
  totalRedemptions: number;
  uniqueUsers: number;
  validFrom: string;
  validTo: string;
  isActive: string;
  createdAt: string;
}

export class ExcelExport {
  /**
   * Convert military time to standard 12-hour format
   * @param timeSlot Time in format like "13:00:00.0" or "13:00"
   * @returns Time in format like "1:00 PM"
   */
  private static convertToStandardTime(timeSlot: string): string {
    // Remove milliseconds and seconds if present
    const timeParts = timeSlot.split(':');
    if (timeParts.length < 2) return timeSlot;

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    const period = hours >= 12 ? 'PM' : 'AM';
    const standardHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    // Only show minutes if they're not zero
    if (minutes === 0) {
      return `${standardHours} ${period}`;
    }

    return `${standardHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Calculate delivery time (30 minutes after given time)
   * @param timeSlot Time in format like "13:00:00.0" or "13:00"
   * @returns Delivery time in standard format
   */
  private static calculateDeliveryTime(timeSlot: string): string {
    const timeParts = timeSlot.split(':');
    if (timeParts.length < 2) return '';

    let hours = parseInt(timeParts[0], 10);
    let minutes = parseInt(timeParts[1], 10);

    // Add 30 minutes
    minutes += 30;

    // Handle overflow
    if (minutes >= 60) {
      hours += Math.floor(minutes / 60);
      minutes = minutes % 60;
    }

    // Handle 24-hour overflow
    if (hours >= 24) {
      hours = hours % 24;
    }

    // Convert to standard time format
    const period = hours >= 12 ? 'PM' : 'AM';
    const standardHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    // Only show minutes if they're not zero
    if (minutes === 0) {
      return `${standardHours} ${period}`;
    }

    return `${standardHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Export party data to Excel format
   * @param parties Array of BookedParty objects
   * @param filename Optional filename for the download
   */
  static exportPartiesToExcel(parties: BookedParty[], filename?: string): void {
    if (!parties || parties.length === 0) {
      console.warn('No parties data to export');
      return;
    }

    // Get all unique extra names to create dynamic columns
    const allExtraNames = new Set<string>();
    parties.forEach(party => {
      if (party.bookedPartyExtras) {
        party.bookedPartyExtras.forEach(extra => {
          allExtraNames.add(extra.name);
        });
      }
    });

    // Convert parties to Excel format
    const excelData: ExcelExportData[] = parties.map(party => {
      const data: ExcelExportData = {
        date: this.formatDateForExcel(party.sessionDate),
        time: this.convertToStandardTime(party.timeSlot),
        delivery: this.calculateDeliveryTime(party.timeSlot),
        customerName: party.customerName,
        customerEmail: party.customerEmail,
        customerPhone: party.customerPhone,
        guestOfHonor: party.guestName || '',
        notes: party.notes || '',
        packageName: party.name,
        pizzaList: party.pizzaList || '',
      };

      // Add dynamic columns for extras
      allExtraNames.forEach(extraName => {
        const extra = party.bookedPartyExtras?.find(e => e.name === extraName);
        data[extraName] = extra ? extra.quantity : 0;
      });

      return data;
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    console.log('Excel Data:', excelData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 12 }, // Date
      { wch: 10 }, // Time
      { wch: 10 }, // Delivery
      { wch: 20 }, // Customer Name
      { wch: 25 }, // Customer Email
      { wch: 15 }, // Customer Phone
      { wch: 20 }, // Guest of Honor
      { wch: 30 }, // Notes
      { wch: 20 }, // Package Name
      { wch: 25 }, // Pizza List
    ];

    // Add widths for extra columns
    allExtraNames.forEach(() => {
      columnWidths.push({ wch: 15 });
    });

    worksheet['!cols'] = columnWidths;

    // Add the worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Booked Parties');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const defaultFilename = `booked-parties-${timestamp}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write and download the file
    XLSX.writeFile(workbook, finalFilename);
  }

  static exportSessionsToExcel(
    sessions: SkatingSession[],
    filename?: string,
  ): void {
    if (!sessions || sessions.length === 0) {
      console.warn('No sessions data to export');
      return;
    }

    // Convert sessions to Excel format
    const excelData: SkatingSessionExportData[] = sessions.map(session => ({
      date: this.formatDateForExcel(session.sessionDate),
      time: this.convertToStandardTime(session.timeSlot),
      sessionNumber: session.skatingSessionId,
      barcodeId: session.barcodeId || '',
      customerName: session.customerName || '',
      customerEmail: session.customerEmail || '',
      customerPhone: session.customerPhone || '',
      waiver: session.waiverUrl ? 'X' : '-',
      redeemed: session.redeemed ? 'Yes' : 'No',
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 12 }, // Date
      { wch: 10 }, // Time
      { wch: 12 }, // Session Number
      { wch: 15 }, // Barcode ID
      { wch: 20 }, // Customer Name
      { wch: 25 }, // Customer Email
      { wch: 15 }, // Customer Phone
      { wch: 8 }, // Waiver
      { wch: 10 }, // Redeemed
    ];

    worksheet['!cols'] = columnWidths;

    // Add the worksheet to workbook
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Booked Skating Sessions',
    );

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const defaultFilename = `booked-skating-sessions-${timestamp}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write and download the file
    XLSX.writeFile(workbook, finalFilename);
  }

  /**
   * Format date for Excel export
   * @param dateString ISO date string
   * @returns Formatted date string
   */
  static formatDateForExcel(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  /**
   * Format time for Excel export
   * @param timeSlot Time slot string
   * @returns Formatted time string
   */
  static formatTimeForExcel(timeSlot: string): string {
    // If timeSlot is already in a good format, return as is
    // Otherwise, you might need to parse and format it
    return timeSlot;
  }

  /**
   * Export coupon data to Excel format
   * @param coupons Array of Coupon objects
   * @param filename Optional filename for the download
   */
  static exportCouponsToExcel(coupons: Coupon[], filename?: string): void {
    if (!coupons || coupons.length === 0) {
      console.warn('No coupons data to export');
      return;
    }

    // Convert coupons to Excel format
    const excelData: CouponExportData[] = coupons.map(coupon => ({
      couponCode: coupon.couponCode,
      name: coupon.name,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      maxUses: coupon.maxUses,
      maxUsesPerUser: coupon.maxUsesPerUser,
      totalRedemptions: coupon.totalRedemptions,
      uniqueUsers: coupon.uniqueUsers,
      validFrom: this.formatDateForExcel(coupon.validFrom),
      validTo: this.formatDateForExcel(coupon.validTo),
      isActive: coupon.isActive ? 'Yes' : 'No',
      createdAt: this.formatDateForExcel(coupon.createdAt),
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 15 }, // Coupon Code
      { wch: 25 }, // Name
      { wch: 15 }, // Discount Type
      { wch: 12 }, // Discount Value
      { wch: 10 }, // Max Uses
      { wch: 15 }, // Max Uses Per User
      { wch: 15 }, // Total Redemptions
      { wch: 12 }, // Unique Users
      { wch: 12 }, // Valid From
      { wch: 12 }, // Valid To
      { wch: 10 }, // Is Active
      { wch: 12 }, // Created At
    ];

    worksheet['!cols'] = columnWidths;

    // Add the worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Active Coupons');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const defaultFilename = `active-coupons-${timestamp}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write and download the file
    XLSX.writeFile(workbook, finalFilename);
  }
}
